/**
 * Robot - Enemy robot entity with AI, patrol, detection, and attack behaviors
 * Represents the Khan robots that hunt the player
 */

import { Entity } from './Entity';
import type { Vector2 } from '../../types/game';
import { getPhysicsEngine } from '../../engine/PhysicsEngine';
import { getCollisionSystem } from '../../engine/CollisionSystem';
import type { Player } from './Player';
import { playLandingSound, playJumpSound } from '../audio/SoundEffects';

export type RobotType = 'basic' | 'fast' | 'heavy' | 'stealth' | 'ranged' | 'boss'
  | 'khan_prime' | 'khan_beta' | 'khan_gamma' | 'khan_omega'
  | 'stalker' | 'crawler' | 'phantom'
  | 'hunter' | 'runner' | 'tank' | 'ambusher' | 'tracker' | 'mimic';

export type RobotAIState = 'idle' | 'patrol' | 'investigate' | 'chase' | 'attack' | 'search' | 'return';

export type RobotSpecialAbility = 'none' | 'ceiling_crawler' | 'vent_traversal';

export interface RobotMemory {
  playerHidingSpots: Vector2[];
  encounterLocations: Vector2[];
  lastSeenTime: number;
  searchedAreas: Vector2[];
  ambushPoints: Vector2[];
}

export class Robot extends Entity {
  // Robot type and AI
  public robotType: RobotType;
  public aiState: RobotAIState = 'patrol';

  // Detection and combat
  public detectionRange: number = 300; // pixels
  public attackRange: number = 50; // pixels
  public damage: number = 25; // damage per attack
  public isAggressive: boolean = false;

  // Movement
  public moveSpeed: number = 100; // pixels/second
  public chaseSpeed: number = 200; // pixels/second
  public facingDirection: 'left' | 'right' = 'right';

  // Special ability (differentiates robot types)
  public specialAbility: RobotSpecialAbility = 'none';
  public instantKill: boolean = false;
  public robotColor: number = 0xFF2200;

  // AI tracking
  public targetPosition: Vector2 | null = null;
  public lastKnownPlayerPosition: Vector2 | null = null;
  public patrolPath: Vector2[] = [];
  public currentPatrolIndex: number = 0;

  // Animation
  public currentAnimation: string = 'idle';

  // AI memory (learning system)
  public memory: RobotMemory = {
    playerHidingSpots: [],
    encounterLocations: [],
    lastSeenTime: 0,
    searchedAreas: [],
    ambushPoints: [],
  };

  // AI timers
  private stateTimer: number = 0;
  private patrolPauseTimer: number = 0;
  private investigateTimer: number = 0;
  private searchTimer: number = 0;

  // AI configuration
  private patrolPauseDuration: number = 0.5; // seconds
  private investigateDuration: number = 5; // seconds
  private searchDuration: number = 10; // seconds
  private attackCooldown: number = 1; // seconds
  private lastAttackTime: number = 0;

  constructor(
    id: string,
    robotType: RobotType,
    position: Vector2,
    size: { width: number; height: number }
  ) {
    super(id, 'robot', position, size);

    this.robotType = robotType;
    this.health = 100;
    this.maxHealth = 100;

    // Set robot-specific properties based on type
    this.configureRobotType(robotType);
  }

  /**
   * Configure robot stats and abilities based on type.
   *
   * FILE: src/game/entities/Robot.ts  (this file)
   * SPAWN / PATROL: src/game/scenes/TestScene.ts → createRobots()
   *
   * ── ACTIVE NIGHT ROBOTS ─────────────────────────────────────────────────────
   *
   *  stalker  (Night 1+) — RED
   *    • Standard ground patrol on Floor 1
   *    • Normal vision cone (320px), 25 damage per hit
   *    • Can open and teleport through unlocked doors while chasing (canOpenDoors = true)
   *    • No special ability — relies on persistence and door access
   *
   *  crawler  (Night 2+) — BLUE
   *    • Ground patrol on Floor 3, uses jump-based navigation like all robots
   *    • Wide detection range (360px), INSTANT KILL on contact
   *    • No special ability flag — straightforward ground chaser
   *    • High threat: one touch = game over
   *
   *  phantom  (Night 3+) — PURPLE
   *    • Fastest robot — outpaces the player when chasing
   *    • Teleports through vents during chase (specialAbility = 'vent_traversal')
   *    • Can shortcut between rooms the player thinks are safe
   *    • 40 damage per hit (not instant kill, but hits hard)
   *
   * ── LEGACY / RESERVED TYPES ─────────────────────────────────────────────────
   *  basic       — placeholder, same stats as stalker
   *  fast/runner — very fast, short detection, light damage
   *  heavy/tank  — very slow, high damage, long detection
   *  stealth/mimic — slow, ignores mask disguise, long detection
   *  ranged      — slow, very long detection, fires projectiles (not yet wired)
   *  hunter      — balanced chaser, slightly stronger than stalker
   *  ambusher    — slow patrol, explosive chase speed — punishes sprinting
   *  tracker     — longest detection range, memorises player routes
   *  boss/khan_prime — boss tier, high damage, long range
   *  khan_beta   — slightly weaker than prime
   *  khan_gamma  — medium boss
   *  khan_omega  — second strongest, pink tint
   * ────────────────────────────────────────────────────────────────────────────
   */
  private configureRobotType(type: RobotType): void {
    switch (type) {
      // ── Active night robots ──────────────────────────────────────────
      case 'stalker':
        // Red, ground patrol, door-opener, normal damage, Night 1+
        this.moveSpeed = 150;
        this.chaseSpeed = 260;
        this.detectionRange = 320;
        this.damage = 25;
        this.specialAbility = 'none';
        this.instantKill = false;
        this.robotColor = 0xFF2200;
        break;

      case 'crawler':
        // Blue, ground patrol Floor 3, instant-kill on contact, Night 2+
        this.moveSpeed = 130;
        this.chaseSpeed = 220;
        this.detectionRange = 360;
        this.damage = 9999;
        this.specialAbility = 'none';
        this.instantKill = true;
        this.robotColor = 0x0066FF;
        break;

      case 'phantom':
        // Purple, vent traversal shortcut while chasing, fast, Night 3+
        this.moveSpeed = 180;
        this.chaseSpeed = 320;
        this.detectionRange = 400;
        this.damage = 40;
        this.specialAbility = 'vent_traversal';
        this.instantKill = false;
        this.robotColor = 0x9900CC;
        break;

      // ── Legacy / reserved types ──────────────────────────────────────
      case 'basic':
        // Placeholder — same role as stalker
        this.moveSpeed = 200;
        this.chaseSpeed = 300;
        this.detectionRange = 300;
        this.damage = 25;
        this.robotColor = 0xFF0000;
        break;

      case 'fast':
      case 'runner':
        // Very fast, short detection, light damage
        this.moveSpeed = 300;
        this.chaseSpeed = 450;
        this.detectionRange = 250;
        this.damage = 20;
        this.robotColor = 0xFF6600;
        break;

      case 'heavy':
      case 'tank':
        // Very slow, wide detection, high damage
        this.moveSpeed = 75;
        this.chaseSpeed = 100;
        this.detectionRange = 350;
        this.damage = 40;
        this.robotColor = 0xAA0000;
        break;

      case 'stealth':
      case 'mimic':
        // Slow, long detection, ignores mask disguise
        this.moveSpeed = 80;
        this.chaseSpeed = 180;
        this.detectionRange = 400;
        this.damage = 30;
        this.robotColor = 0x444444;
        break;

      case 'ranged':
        // Slow mover, longest detection, fires projectiles (not yet wired)
        this.moveSpeed = 90;
        this.chaseSpeed = 140;
        this.detectionRange = 450;
        this.damage = 15;
        this.robotColor = 0xFF9900;
        break;

      case 'hunter':
        // Balanced — slightly stronger and faster than stalker
        this.moveSpeed = 160;
        this.chaseSpeed = 280;
        this.detectionRange = 340;
        this.damage = 30;
        this.robotColor = 0xCC3300;
        break;

      case 'ambusher':
        // Slow patrol, explosive chase speed — punishes the player for sprinting
        this.moveSpeed = 100;
        this.chaseSpeed = 350;
        this.detectionRange = 380;
        this.damage = 35;
        this.robotColor = 0xFF3300;
        break;

      case 'tracker':
        // Longest detection range, memorises player routes via AI learning
        this.moveSpeed = 140;
        this.chaseSpeed = 240;
        this.detectionRange = 500;
        this.damage = 20;
        this.robotColor = 0xFF4400;
        break;

      case 'boss':
      case 'khan_prime':
        // Boss tier — high damage, long range, Night 7 finale
        this.moveSpeed = 120;
        this.chaseSpeed = 200;
        this.detectionRange = 400;
        this.damage = 50;
        this.robotColor = 0xFF0000;
        break;

      case 'khan_beta':
        // Slightly weaker than prime
        this.moveSpeed = 110;
        this.chaseSpeed = 190;
        this.detectionRange = 380;
        this.damage = 35;
        this.robotColor = 0xFF0044;
        break;

      case 'khan_gamma':
        // Medium boss
        this.moveSpeed = 130;
        this.chaseSpeed = 210;
        this.detectionRange = 360;
        this.damage = 30;
        this.robotColor = 0xFF0088;
        break;

      case 'khan_omega':
        // Second strongest boss, pink tint
        this.moveSpeed = 140;
        this.chaseSpeed = 220;
        this.detectionRange = 420;
        this.damage = 45;
        this.robotColor = 0xFF00CC;
        break;
    }
  }

  /**
   * Register robot with physics engine — all robots use full gravity and can jump.
   */
  protected override registerPhysics(): void {
    const physicsEngine = getPhysicsEngine();
    physicsEngine.addEntity(this as any, {
      mass: 2.0,
      gravity: 1.0,
      friction: 0.8,
      restitution: 0.0,
      isKinematic: false,
      isGrounded: false,
      canJump: true,
    });
  }

  /**
   * Register robot with collision system
   */
  protected override registerCollision(): void {
    const collisionSystem = getCollisionSystem();
    collisionSystem.addCollider(this as any, {
      type: 'box',
      isTrigger: false,
      layer: 'robot',
    });
  }

  /**
   * Update robot AI and behavior
   */
  public update(deltaTime: number): void {
    if (this.destroyed || !this.active) return;

    this.stateTimer += deltaTime;

    // Update AI based on current state
    switch (this.aiState) {
      case 'idle':
        this.updateIdle(deltaTime);
        break;
      case 'patrol':
        this.updatePatrol(deltaTime);
        break;
      case 'investigate':
        this.updateInvestigate(deltaTime);
        break;
      case 'chase':
        this.updateChase(deltaTime);
        break;
      case 'attack':
        this.updateAttack(deltaTime);
        break;
      case 'search':
        this.updateSearch(deltaTime);
        break;
      case 'return':
        this.updateReturn(deltaTime);
        break;
    }

    // Update animation
    this.updateAnimation();
  }

  /**
   * Render robot (override with Three.js sprite)
   */
  public render(): void {
    // To be implemented with Three.js sprite rendering
  }

  /**
   * Update idle state
   */
  private updateIdle(_deltaTime: number): void {
    const physicsEngine = getPhysicsEngine();
    physicsEngine.moveHorizontal(this.id, 0, 0);

    if (this.stateTimer > 1) {
      this.transitionToPatrol();
    }
  }

  /**
   * Update patrol state
   */
  private updatePatrol(deltaTime: number): void {
    if (this.patrolPath.length === 0) {
      this.transitionToIdle();
      return;
    }

    if (this.patrolPauseTimer > 0) {
      this.patrolPauseTimer -= deltaTime;
      const physicsEngine = getPhysicsEngine();
      physicsEngine.moveHorizontal(this.id, 0, 0);
      return;
    }

    const waypoint = this.patrolPath[this.currentPatrolIndex];
    if (!waypoint) return;
    const distance = this.distanceTo({ position: waypoint } as Entity);

    if (distance < 20) {
      this.patrolPauseTimer = this.patrolPauseDuration;
      this.currentPatrolIndex = (this.currentPatrolIndex + 1) % this.patrolPath.length;
    } else {
      this.moveTowardWithJump(waypoint, this.moveSpeed);
    }
  }

  /**
   * Update investigate state
   */
  private updateInvestigate(deltaTime: number): void {
    this.investigateTimer += deltaTime;

    if (this.targetPosition) {
      const distance = this.distanceTo({ position: this.targetPosition } as Entity);

      if (distance < 30) {
        this.investigateTimer = 0;
        this.transitionToSearch();
      } else {
        this.moveToward(this.targetPosition, this.moveSpeed);
      }
    }

    if (this.investigateTimer > this.investigateDuration) {
      this.transitionToReturn();
    }
  }

  /**
   * Update chase state
   */
  private updateChase(_deltaTime: number): void {
    if (!this.lastKnownPlayerPosition) {
      this.transitionToSearch();
      return;
    }

    const distance = this.distanceTo({ position: this.lastKnownPlayerPosition } as Entity);

    if (distance < this.attackRange) {
      this.transitionToAttack();
    } else if (distance < 50) {
      this.transitionToSearch();
    } else {
      this.moveTowardWithJump(this.lastKnownPlayerPosition, this.chaseSpeed);
    }
  }

  /**
   * Update attack state
   */
  private updateAttack(_deltaTime: number): void {
    const currentTime = Date.now() / 1000;

    const physicsEngine = getPhysicsEngine();
    physicsEngine.moveHorizontal(this.id, 0, 0);

    if (currentTime - this.lastAttackTime >= this.attackCooldown) {
      this.performAttack();
      this.lastAttackTime = currentTime;
    }

    if (this.lastKnownPlayerPosition) {
      const distance = this.distanceTo({ position: this.lastKnownPlayerPosition } as Entity);
      if (distance > this.attackRange * 1.5) {
        this.transitionToChase();
      }
    }
  }

  /**
   * Update search state
   */
  private updateSearch(deltaTime: number): void {
    this.searchTimer += deltaTime;

    // Wander toward the current search target
    if (this.targetPosition) {
      const dist = this.distanceTo({ position: this.targetPosition } as Entity);
      if (dist < 30) {
        // Reached wander point — pick a new nearby spot to keep roaming
        this.targetPosition = {
          x: this.position.x + (Math.random() - 0.5) * 250,
          y: this.position.y,
        };
      } else {
        this.moveTowardWithJump(this.targetPosition, this.moveSpeed * 0.65);
      }
    }

    if (this.searchTimer > this.searchDuration) {
      this.searchTimer = 0;
      this.transitionToReturn();
    }
  }

  /**
   * Update return state
   */
  private updateReturn(_deltaTime: number): void {
    if (this.patrolPath.length > 0) {
      this.transitionToPatrol();
    } else {
      this.transitionToIdle();
    }
  }

  /**
   * Move toward a target position (no jumping)
   */
  private moveToward(target: Vector2, speed: number): void {
    const direction = this.directionTo({ position: target } as Entity);
    const physicsEngine = getPhysicsEngine();

    if (direction.x !== 0) {
      this.facingDirection = direction.x > 0 ? 'right' : 'left';
      physicsEngine.moveHorizontal(this.id, direction.x, speed);
    }
  }

  /**
   * Move toward a target with jumping to avoid edges and reach platforms
   */
  private moveTowardWithJump(target: Vector2, speed: number): void {
    const direction = this.directionTo({ position: target } as Entity);
    const physicsEngine = getPhysicsEngine();

    if (direction.x === 0) return;

    this.facingDirection = direction.x > 0 ? 'right' : 'left';

    const atEdge = this.isAtPlatformEdge(direction.x);
    const targetHigher = target.y > this.position.y + 20;

    if (atEdge && physicsEngine.isGrounded(this.id)) {
      const jumpForce = targetHigher ? 1400 : 1300;
      const success = physicsEngine.jump(this.id, jumpForce);
      if (success) {
        const t = Math.min(this.distanceToPlayer / 700, 1);
        playJumpSound(1 - t * t); // ← distance volume
      }
    } else if (targetHigher && physicsEngine.isGrounded(this.id) && !atEdge) {
      const success = physicsEngine.jump(this.id, 1300);
      if (success) {
        const t = Math.min(this.distanceToPlayer / 700, 1);
        playJumpSound(1 - t * t); // ← distance volume
      }
    }

    physicsEngine.moveHorizontal(this.id, direction.x, speed);
  }

  /**
   * Check if robot is at a platform edge
   */
  private isAtPlatformEdge(directionX: number): boolean {
    const collisionSystem = getCollisionSystem();
    const physicsEngine = getPhysicsEngine();

    if (!physicsEngine.isGrounded(this.id)) {
      return false;
    }

    const checkDistance = this.size.width / 2 + 20;
    const checkX = this.position.x + (directionX > 0 ? checkDistance : -checkDistance);
    const checkYStart = this.position.y - this.size.height / 2 - 5;

    const rayOrigin = { x: checkX, y: checkYStart };
    const rayDirection = { x: 0, y: -1 };
    const maxDistance = 100;

    const hit = collisionSystem.raycast(rayOrigin, rayDirection, maxDistance, ['static']);
    return !hit;
  }

  /**
   * Perform attack on player
   */
  private performAttack(): void {
    this.currentAnimation = 'attack';
  }

  /**
   * Detect player
   */
  public detectPlayer(player: Player): boolean {
    if (player.isMaskOn()) return false;
    if (player.isPlayerHiding()) return false;

    const distance = this.distanceTo(player);
    return distance <= this.detectionRange;
  }

  /**
   * React to player detection
   */
  public onPlayerDetected(player: Player): void {
    this.lastKnownPlayerPosition = { ...player.position };
    this.isAggressive = true;

    this.memory.encounterLocations.push({ ...player.position });
    this.memory.lastSeenTime = Date.now();

    this.transitionToChase();
  }

  /**
   * React to noise (player running, doors, etc.)
   */
  public onNoiseHeard(noisePosition: Vector2): void {
    const distance = this.distanceTo({ position: noisePosition } as Entity);

    if (distance < 600) {
      this.targetPosition = { ...noisePosition };
      this.transitionToInvestigate();
    }
  }

  /**
   * Set patrol path
   */
  public setPatrolPath(path: Vector2[]): void {
    this.patrolPath = [...path];
    this.currentPatrolIndex = 0;
  }

  /**
   * Update animation state
   */
  private updateAnimation(): void {
    const isMoving = Math.abs(this.velocity.x) > 1;

    switch (this.aiState) {
      case 'idle':
        this.currentAnimation = 'idle';
        break;
      case 'patrol':
        this.currentAnimation = isMoving ? 'walk' : 'idle';
        break;
      case 'investigate':
      case 'search':
        this.currentAnimation = isMoving ? 'walk' : 'search';
        break;
      case 'chase':
        this.currentAnimation = 'run';
        break;
      case 'attack':
        this.currentAnimation = 'attack';
        break;
      case 'return':
        this.currentAnimation = isMoving ? 'walk' : 'idle';
        break;
    }
  }

  // State transitions (public so TestScene/RobotAI can call them)
  public transitionToIdle(): void {
    this.aiState = 'idle';
    this.stateTimer = 0;
    this.isAggressive = false;
  }

  public transitionToPatrol(): void {
    this.aiState = 'patrol';
    this.stateTimer = 0;
    this.isAggressive = false;
  }

  public transitionToInvestigate(): void {
    this.aiState = 'investigate';
    this.stateTimer = 0;
    this.investigateTimer = 0;
  }

  public transitionToChase(): void {
    this.aiState = 'chase';
    this.stateTimer = 0;
    this.isAggressive = true;
  }

  public transitionToAttack(): void {
    this.aiState = 'attack';
    this.stateTimer = 0;
  }

  public transitionToSearch(): void {
    this.aiState = 'search';
    this.stateTimer = 0;
    this.searchTimer = 0;
    // Pick a random wander point near the last known player position
    const base = this.lastKnownPlayerPosition ?? this.position;
    this.targetPosition = {
      x: base.x + (Math.random() - 0.5) * 300,
      y: base.y,
    };
  }

  public transitionToReturn(): void {
    this.aiState = 'return';
    this.stateTimer = 0;
    this.targetPosition = null;
    this.lastKnownPlayerPosition = null;
    this.isAggressive = false;
    // Signal to external systems (e.g. TestScene) that detection should be suppressed
    // for a cooldown period. The flag is read and cleared by the update loop.
    this.returnedFromSearch = true;
  }

  // Set to true when robot finishes a search and returns to patrol.
  // TestScene reads this to apply a re-detection cooldown.
  public returnedFromSearch: boolean = false;

  // If true, this robot will open closed (unlocked) doors while chasing.
  public canOpenDoors: boolean = false;

  // Set by TestScene each frame — used to scale robot sounds by distance
  public distanceToPlayer: number = 0; // ← distance volume

  /**
   * Called when robot lands on the ground
   */
  protected override onLanding(landingVelocity: number): void {
    const t = Math.min(this.distanceToPlayer / 700, 1);
    const vol = 1 - t * t; // ← distance volume: full at 0px, silent at 700px
    playLandingSound(landingVelocity, vol);
  }

  // Getters
  public getAIState(): RobotAIState {
    return this.aiState;
  }

  public getRobotType(): RobotType {
    return this.robotType;
  }

  public isRobotAggressive(): boolean {
    return this.isAggressive;
  }

  public getDetectionRange(): number {
    return this.detectionRange;
  }

  public getDamage(): number {
    return this.damage;
  }
}
